/**
 * One ICF level vocabulary for every screen (CORE-2).
 *
 * Screens used to infer a level by substring-matching the *translated label*
 * ("Below ACC" contains "acc", "approaching MCC" contains "mcc", English
 * "No data" does not contain Turkish "veri"), which highlighted the wrong
 * level. Everything now goes through `normalizeLevelKey()` → `LEVEL_META`,
 * and bar positions come from the key, never from display text.
 */

import { brand } from '../design-system/theme/tokens';

export type LevelKey =
  | 'below_acc'
  | 'acc'
  | 'ready_for_pcc'
  | 'pcc'
  | 'pcc_upper_mcc_adjacent'
  | 'mcc';

export interface LevelMeta {
  /** Full display label (i18n key). */
  labelKey: string;
  /** Short code for tight spaces (e.g. the 390px hero bar). Not translated: ICF codes. */
  shortLabel: string;
  /** Strict order, lowest first. */
  ordinal: number;
  /**
   * Segment of the 4-band progress bar (ACC · PCC prep · PCC · MCC).
   * -1 means "below the first band" (nothing is highlighted).
   */
  bandIndex: number;
  color: string;
  softColor: string;
}

export const LEVEL_META: Record<LevelKey, LevelMeta> = {
  below_acc: {
    labelKey: 'icfProgress.levelBelowACC',
    shortLabel: '< ACC',
    ordinal: 0,
    bandIndex: -1,
    color: '#B42318',
    softColor: '#FEF3F2',
  },
  acc: {
    labelKey: 'icfProgress.levelACC',
    shortLabel: 'ACC',
    ordinal: 1,
    bandIndex: 0,
    color: '#9A6500',
    softColor: '#FFF4D9',
  },
  ready_for_pcc: {
    labelKey: 'icfProgress.webLevelNearPcc',
    shortLabel: 'PCC prep',
    ordinal: 2,
    bandIndex: 1,
    color: '#5B6CC7',
    softColor: '#F2F4FF',
  },
  pcc: {
    labelKey: 'icfProgress.levelPCC',
    shortLabel: 'PCC',
    ordinal: 3,
    bandIndex: 2,
    color: brand.primary,
    softColor: brand.primarySoft,
  },
  pcc_upper_mcc_adjacent: {
    labelKey: 'icfProgress.levelUpperPccMccAdjacent',
    shortLabel: 'PCC+',
    ordinal: 4,
    bandIndex: 2,
    color: '#1F3A9E',
    softColor: '#E7ECFF',
  },
  mcc: {
    labelKey: 'icfProgress.levelMCC',
    shortLabel: 'MCC',
    ordinal: 5,
    bandIndex: 3,
    color: '#2F9A76',
    softColor: '#EAF7F1',
  },
};

/** The 4 bands of the progress hero bar, in order. */
export const LEVEL_BANDS: LevelKey[] = ['acc', 'ready_for_pcc', 'pcc', 'mcc'];

/**
 * Exact aliases (canonical keys, backend variants, and the EN/TR display labels
 * that older payloads persisted). Matching is exact after normalisation — never
 * by substring — so "Below ACC" can't light up ACC.
 */
const ALIASES: Record<string, LevelKey> = {
  below_acc: 'below_acc',
  below: 'below_acc',
  acc_alti: 'below_acc',
  'acc_altı': 'below_acc',

  acc: 'acc',
  'acc+': 'acc',
  acc_level: 'acc',
  'acc_düzeyi': 'acc',

  ready_for_pcc: 'ready_for_pcc',
  near_pcc: 'ready_for_pcc',
  "pcc'ye_hazır": 'ready_for_pcc',
  "pcc'ye_yakın": 'ready_for_pcc',
  pcc_preparation_band: 'ready_for_pcc',
  'pcc_hazırlık_bandı': 'ready_for_pcc',

  pcc: 'pcc',
  pcc_level: 'pcc',
  'pcc_düzeyi': 'pcc',

  pcc_upper_mcc_adjacent: 'pcc_upper_mcc_adjacent',
  'upper_pcc_/_approaching_mcc_profile': 'pcc_upper_mcc_adjacent',
  'pcc_üstü_/_mcc\'ye_yaklaşan_profil': 'pcc_upper_mcc_adjacent',

  mcc: 'mcc',
  mcc_level: 'mcc',
  'mcc_düzeyi': 'mcc',
};

/**
 * Map any level value (canonical key, `*_level_key` field, legacy display
 * label) to a `LevelKey`. Returns null for empty values (C1/C2 carry `""`),
 * "no data" placeholders and anything unknown.
 */
export function normalizeLevelKey(raw: unknown): LevelKey | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw
    .trim()
    .replace(/[’‘]/g, "'")
    .toLocaleLowerCase('tr-TR')
    .replace(/[\s-]+/g, '_');
  if (!normalized) return null;
  // `toLocaleLowerCase('tr-TR')` maps "I" to "ı"; ICF codes contain no I,
  // but make the ASCII form match too.
  const ascii = normalized.replace(/ı/g, 'i');
  return ALIASES[normalized] ?? ALIASES[ascii] ?? null;
}

export function levelMeta(raw: unknown): LevelMeta | null {
  const key = normalizeLevelKey(raw);
  return key ? LEVEL_META[key] : null;
}

/** Band index for the 4-segment bar; -1 when nothing should be highlighted. */
export function levelBandIndex(raw: unknown): number {
  return levelMeta(raw)?.bandIndex ?? -1;
}

/** Short band label for tight spaces (ICF codes; the PCC-prep band is translated). */
export function shortLevelLabel(t: (key: string) => string, key: LevelKey): string {
  return key === 'ready_for_pcc' ? t('report.levelShortReadyForPcc') : LEVEL_META[key].shortLabel;
}

/**
 * Translate a level for display. Unknown values fall back to `fallbackKey`
 * (defaults to "No data"), never to the raw backend string.
 */
export function formatLevel(
  t: (key: string) => string,
  raw: unknown,
  fallbackKey = 'icfProgress.noData',
): string {
  const meta = levelMeta(raw);
  return t(meta ? meta.labelKey : fallbackKey);
}
